import React, { useState, useEffect } from 'react';
import { Card, CardBody, Typography, Input, Button, Checkbox } from "@material-tailwind/react";
import axios from 'axios';
import { FaTrash, FaPlus, FaTimes, FaPenAlt, FaToggleOn, FaToggleOff } from 'react-icons/fa';
import { useAuth } from '../AuthContext';
const { rootUrl } = require('../../../config/config');

const VatRates = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState({
        vat_code: '',
        vat_name: '',
        vat_rate: '',
        is_active: true,
    });
    const { user } = useAuth();

    const token = localStorage.getItem('authToken') || '';

    const fetchItems = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${rootUrl}/system/vat-rates`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setItems(response.data || []);
            setError('');
        } catch (err) {
            setError('Failed to fetch VAT rates.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        fetchItems();
    }, []);

    const startEdit = (item) => {
        setEditingId(item.vat_id);
        setForm({
            vat_code: item.vat_code || '',
            vat_name: item.vat_name || '',
            vat_rate: item.vat_rate?.toString() ?? '',
            is_active: !!item.is_active,
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const cancelForm = () => {
        setEditingId(null);
        setForm({ vat_code: '', vat_name: '', vat_rate: '', is_active: true });
        setError('');
    };

    const deleteItem = async (vat_id) => {
        if (!window.confirm('Delete this VAT rate?')) return;
        try {
            await axios.delete(`${rootUrl}/system/vat-rates/${vat_id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            fetchItems();
        } catch (err) {
            setError('Failed to delete VAT rate.');
            console.error(err);
        }
    };

    const toggleActive = async (item) => {
        try {
            await axios.patch(
                `${rootUrl}/system/vat-rates/${item.vat_id}/toggle`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            fetchItems();
        } catch (err) {
            setError('Failed to toggle VAT status.');
            console.error(err);
        }
    };

    const validateForm = () => {
        const code = form.vat_code.trim();
        const name = form.vat_name.trim();
        const rateStr = String(form.vat_rate).trim();
        if (!code || !name || rateStr === '') return 'VAT code, name, and rate are required.';
        const rate = Number(rateStr);
        if (Number.isNaN(rate)) return 'VAT rate must be a number.';
        if (rate < 0 || rate > 100) return 'VAT rate must be between 0 and 100.';
        return '';
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationError = validateForm();
        if (validationError) { setError(validationError); return; }

        try {
            const payload = {
                vat_code: form.vat_code.toUpperCase(),
                vat_name: form.vat_name,
                vat_rate: Number(form.vat_rate),
                is_active: !!form.is_active,
                ...(editingId ? { edit_by: user?.username || 'system' } : { input_by: user?.username || 'system' }),
            };

            if (editingId) {
                await axios.put(`${rootUrl}/system/vat-rates/${editingId}`, payload, {
                    headers: { Authorization: `Bearer ${token}` },
                });
            } else {
                await axios.post(`${rootUrl}/system/vat-rates`, payload, {
                    headers: { Authorization: `Bearer ${token}` },
                });
            }

            cancelForm();
            fetchItems();
        } catch (err) {
            const msg = err?.response?.data?.message || (editingId ? 'Failed to update VAT rate.' : 'Failed to add VAT rate.');
            setError(msg);
            console.error(err);
        }
    };

    return (
        <div className="w-full max-w-7xl mx-auto mt-6 px-2 sm:px-4">
            <Card className="border-t">
                <CardBody className="p-3 sm:p-6">
                    <h2 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-4 sm:mb-6">VAT Rates</h2>

                    {error && (
                        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">{error}</div>
                    )}

                    <form onSubmit={handleSubmit} className="mb-6 space-y-4">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-gray-700">{editingId ? 'Edit VAT Rate' : 'Add New VAT Rate'}</h3>
                            {editingId && (
                                <Button type="button" variant="outlined" color="gray" onClick={cancelForm} className="flex items-center shadow-none rounded-none">
                                    <FaTimes className="mr-2" /> Cancel
                                </Button>
                            )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <Input type="text" label="VAT Code *" value={form.vat_code} onChange={(e) => setForm({ ...form, vat_code: e.target.value.toUpperCase() })} className="uppercase" style={{ textTransform: 'uppercase' }} required />
                            <Input type="text" label="VAT Name *" value={form.vat_name} onChange={(e) => setForm({ ...form, vat_name: e.target.value })} required />
                            <Input type="number" label="VAT Rate (%) *" value={form.vat_rate} onChange={(e) => setForm({ ...form, vat_rate: e.target.value })} min={0} max={100} step="0.01" required />
                            <div className="flex items-end pb-2">
                                <Checkbox
                                    label="Active"
                                    checked={form.is_active}
                                    onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                                    className="w-5 h-5"
                                    color="blue"
                                />
                            </div>
                        </div>

                        <Button type="submit" className="bg-brand-primary text-white flex items-center shadow-none rounded-none">
                            <FaPlus className="mr-2" /> {editingId ? 'Update VAT Rate' : 'Add VAT Rate'}
                        </Button>
                    </form>

                    {loading ? (
                        <Typography>Loading...</Typography>
                    ) : (
                        <div className="overflow-x-auto -mx-2 sm:mx-0">
                            <table className="w-full table-auto border-collapse min-w-[800px]">
                                <thead>
                                    <tr className="bg-gray-100">
                                        <th className="border px-2 sm:px-4 py-2 text-left text-xs sm:text-sm">ID</th>
                                        <th className="border px-2 sm:px-4 py-2 text-left text-xs sm:text-sm">Code</th>
                                        <th className="border px-2 sm:px-4 py-2 text-left text-xs sm:text-sm">Name</th>
                                        <th className="border px-2 sm:px-4 py-2 text-right text-xs sm:text-sm">Rate (%)</th>
                                        <th className="border px-2 sm:px-4 py-2 text-center text-xs sm:text-sm">Status</th>
                                        <th className="border px-2 sm:px-4 py-2 text-center text-xs sm:text-sm">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((item) => (
                                        <tr key={item.vat_id} className="hover:bg-gray-50">
                                            <td className="border px-2 sm:px-4 py-2"><Typography className="text-xs sm:text-sm">{item.vat_id}</Typography></td>
                                            <td className="border px-2 sm:px-4 py-2"><Typography className="uppercase text-xs sm:text-sm">{item.vat_code}</Typography></td>
                                            <td className="border px-2 sm:px-4 py-2"><Typography className="text-xs sm:text-sm">{item.vat_name}</Typography></td>
                                            <td className="border px-2 sm:px-4 py-2 text-right"><Typography className="text-xs sm:text-sm">{Number(item.vat_rate || 0).toFixed(2)}%</Typography></td>
                                            <td className="border px-2 sm:px-4 py-2 text-center">
                                                <span className={`px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-none text-xs font-medium ${item.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>{item.is_active ? 'Active' : 'Inactive'}</span>
                                            </td>
                                            <td className="border px-2 sm:px-4 py-2 text-center">
                                                <div className="flex flex-wrap gap-1 sm:gap-2 justify-center">
                                                    <Button onClick={() => startEdit(item)} className="bg-blue-600 text-white px-1.5 sm:px-2 py-0.5 sm:py-1 text-xs shadow-none rounded-none" size="sm" disabled={editingId === item.vat_id}>
                                                        <FaPenAlt className="w-2.5 sm:w-3 h-2.5 sm:h-3" />
                                                    </Button>
                                                    <Button onClick={() => toggleActive(item)} className={`${item.is_active ? 'bg-gray-600' : 'bg-green-600'} text-white px-1.5 sm:px-2 py-0.5 sm:py-1 text-xs shadow-none rounded-none`} size="sm">
                                                        {item.is_active ? (<FaToggleOff className="w-3 h-3" />) : (<FaToggleOn className="w-3 h-3" />)}
                                                    </Button>
                                                    <Button onClick={() => deleteItem(item.vat_id)} className="bg-red-600 text-white px-1.5 sm:px-2 py-0.5 sm:py-1 text-xs shadow-none rounded-none" size="sm" disabled={editingId === item.vat_id}>
                                                        <FaTrash className="w-2.5 sm:w-3 h-2.5 sm:h-3" />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardBody>
            </Card>
        </div>
    );
};

export default VatRates;
